// src/pages/MonitoringParserPage.jsx
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import PageTransition from '../components/Shared/PageTransition';
import { useAuth } from '../contexts/AuthContext';
import useWebSocket from '../hooks/useWebSocket';
import { useLanguage } from '../contexts/LanguageContext';

import ServersParserHero from '../components/ServersParser/ServersParserHero';
import ServersParserKpiStats from '../components/ServersParser/ServersParserKpiStats';
import ServersParserConfigCard from '../components/ServersParser/ServersParserConfigCard';
import ServersParserSearchCapsule from '../components/ServersParser/ServersParserSearchCapsule';
import ServersParserToolbar from '../components/ServersParser/ServersParserToolbar';
import ServersParserTable from '../components/ServersParser/ServersParserTable';
import ServersParserTips from '../components/ServersParser/ServersParserTips';
import { useNotification } from '../contexts/NotificationContext';
import TermsOfServiceModal from '../components/Shared/TermsOfServiceModal';
import { handleWebSocketError } from '../utils/websocketErrorHandler';

import {
  INITIAL_PARSED_SERVERS,
  normalizeParsedServer
} from '../components/ServersParser/serversParserData';

import {
  PARSER_TOPICS,
  PARSER_MESSAGE_TYPES,
  getParserAppEndpoint,
  getParserUserQueueTopic
} from '../config/wsParserConfig';

import './ServersParserPage.css';

const MonitoringParserPage = ({ openModal }) => {
  const { language, t } = useLanguage();
  const isEn = language === 'en';

  const { isAuthenticated, user } = useAuth();
  const { isConnected, sendMessage, subscribe } = useWebSocket();

  // Состояние конфигурации парсера
  const [searchType, setSearchType] = useState('vuln'); // 'vuln' | 'params'
  const [selectedVuln, setSelectedVuln] = useState('low'); // 'low' | 'medium' | 'high'
  const [playersMin, setPlayersMin] = useState('');
  const [playersMax, setPlayersMax] = useState('');
  const [versionFilter, setVersionFilter] = useState('');
  const [serverLimit, setServerLimit] = useState(50); // 10 | 30 | 50

  // Состояние поиска и сортировки в таблице
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('online-desc'); // 'online-desc' | 'online-asc' | 'vuln' | 'version'

  // Пул серверов (изначально пустой по запросу пользователя)
  const [allServers, setAllServers] = useState([]);
  const [hasParsed, setHasParsed] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const { showToast } = useNotification();

  // Выбор уровня уязвимости (без преждевременных блокировок на клиенте)
  const handleSelectVuln = useCallback((level) => {
    setSelectedVuln(level);
  }, []);

  // Копирование одного IP
  const handleCopyIp = useCallback((ipPort) => {
    if (!ipPort) return;
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(ipPort).then(() => {
        showToast(isEn ? `IP copied: ${ipPort}` : `IP скопирован: ${ipPort}`);
      }).catch(() => {
        showToast(`IP: ${ipPort}`);
      });
    } else {
      const temp = document.createElement('textarea');
      temp.value = ipPort;
      document.body.appendChild(temp);
      temp.select();
      document.execCommand('copy');
      document.body.removeChild(temp);
      showToast(isEn ? `IP copied: ${ipPort}` : `IP скопирован: ${ipPort}`);
    }
  }, [showToast, isEn]);

  // WebSocket subscriptions
  useEffect(() => {
    if (!isConnected || !isAuthenticated) return;

    const unsubProgress = subscribe(
      getParserUserQueueTopic(PARSER_TOPICS.USER_PROGRESS),
      (message) => {
        if (!message) return;
        const { type, data } = message;

        if (type === PARSER_MESSAGE_TYPES.PARSING_STARTED) {
          setIsProcessing(true);
        } else if (
          type === 'SERVER_BATCH' ||
          type === PARSER_MESSAGE_TYPES.SERVERS_FOUND_BATCH
        ) {
          if (data?.servers && Array.isArray(data.servers)) {
            const normalized = data.servers.map((s, idx) => normalizeParsedServer(s, idx));
            setAllServers(prev => {
              const existingIps = new Set(prev.map(s => `${s.ip}:${s.port}`.toLowerCase()));
              const newlyAdded = normalized.filter(
                s => !existingIps.has(`${s.ip}:${s.port}`.toLowerCase())
              );
              return [...newlyAdded, ...prev];
            });
          }
        } else if (type === PARSER_MESSAGE_TYPES.SERVER_FOUND && data) {
          const norm = normalizeParsedServer(data);
          if (norm) {
            setAllServers(prev => {
              const exists = prev.some(
                s => `${s.ip}:${s.port}`.toLowerCase() === `${norm.ip}:${norm.port}`.toLowerCase()
              );
              return exists ? prev : [norm, ...prev];
            });
          }
        }
      }
    );

    const unsubResult = subscribe(
      getParserUserQueueTopic(PARSER_TOPICS.USER_RESULT),
      (message) => {
        if (!message) return;
        const { type, data } = message;

        if (type === PARSER_MESSAGE_TYPES.PARSING_COMPLETED) {
          setIsProcessing(false);
          if (data?.servers && Array.isArray(data.servers) && data.servers.length > 0) {
            const normalized = data.servers.map((s, idx) => normalizeParsedServer(s, idx));
            setAllServers(prev => {
              const existingIps = new Set(prev.map(s => `${s.ip}:${s.port}`.toLowerCase()));
              const newlyAdded = normalized.filter(
                s => !existingIps.has(`${s.ip}:${s.port}`.toLowerCase())
              );
              return [...newlyAdded, ...prev];
            });
            showToast(isEn ? `Parsed ${data.servers.length} servers` : `Спарсено ${data.servers.length} серверов`);
          } else {
            showToast(isEn ? 'Parsing completed successfully' : 'Парсинг успешно завершен');
          }
          window.dispatchEvent(new CustomEvent('showAiInvite', {
            detail: { message: t('aiAssistantInviteParser') }
          }));
        } else if (type === PARSER_MESSAGE_TYPES.PARSING_STOPPED) {
          setIsProcessing(false);
          showToast(isEn ? 'Parsing stopped' : 'Парсинг остановлен');
        }
      }
    );

    const unsubError = subscribe(
      getParserUserQueueTopic(PARSER_TOPICS.USER_ERROR),
      (errorData, wsError) => {
        setIsProcessing(false);
        handleWebSocketError(
          errorData,
          wsError,
          openModal,
          showToast,
          { defaultTitle: isEn ? 'Parsing Error' : 'Ошибка парсинга', moduleName: 'Servers Parser' }
        );
      }
    );

    return () => {
      unsubProgress?.();
      unsubResult?.();
      unsubError?.();
    };
  }, [isConnected, isAuthenticated, subscribe, showToast, isEn, openModal]);

  // Terms of Service Modal State
  const [isTermsModalOpen, setIsTermsModalOpen] = useState(false);

  // Фактическое выполнение запуска парсинга
  const executeStartParsing = useCallback(() => {
    if (isProcessing) return;
    setHasParsed(true);

    if (isConnected && isAuthenticated) {
      setIsProcessing(true);
      setAllServers([]);
      const payload = {
        searchType: searchType === 'vuln' ? 'vulnerability' : 'parameters',
        vulnerabilityLevel: selectedVuln,
        serverCount: serverLimit,
        minOnline: searchType === 'params' ? (parseInt(playersMin, 10) || 0) : 0,
        maxOnline: searchType === 'params' ? (parseInt(playersMax, 10) || 1000) : 1000,
        versionFilter: searchType === 'params' ? versionFilter.trim() : ''
      };

      sendMessage(getParserAppEndpoint(PARSER_TOPICS.APP_START), payload);
      showToast(isEn ? `Parsing initiated (${serverLimit} servers)...` : `Парсинг запущен (${serverLimit} серверов)...`);
    } else {
      // Автономный режим демонстрации (если WebSocket или авторизация еще не активны)
      setIsProcessing(true);
      setAllServers([]);
      setTimeout(() => {
        setIsProcessing(false);
        const candidates = INITIAL_PARSED_SERVERS.filter(s => {
          if (searchType === 'vuln') {
            return s.vulnLevel === selectedVuln;
          } else {
            const minP = playersMin !== '' ? parseInt(playersMin, 10) : 0;
            const maxP = playersMax !== '' ? parseInt(playersMax, 10) : 999999;
            const verQ = versionFilter.trim().toLowerCase();
            if (s.players < minP || s.players > maxP) return false;
            if (verQ && !s.version.toLowerCase().includes(verQ)) return false;
            return true;
          }
        });
        const pool = candidates.length > 0 ? candidates : INITIAL_PARSED_SERVERS;
        const shuffled = [...pool].sort(() => 0.5 - Math.random()).slice(0, serverLimit);
        setAllServers(shuffled);
        showToast(isEn ? `Parsed ${shuffled.length} servers` : `Спарсено ${shuffled.length} серверов`);
      }, 550);
    }
  }, [
    isProcessing,
    isConnected,
    isAuthenticated,
    searchType,
    selectedVuln,
    serverLimit,
    playersMin,
    playersMax,
    versionFilter,
    sendMessage,
    showToast,
    isEn
  ]);

  // Запуск парсинга (открывает Terms modal)
  const handleStartParsing = useCallback(() => {
    if (isProcessing) return;
    setIsTermsModalOpen(true);
  }, [isProcessing]);

  // Фильтрация и сортировка данных таблицы
  const filteredAndSortedServers = useMemo(() => {
    const minP = playersMin !== '' ? parseInt(playersMin, 10) : 0;
    const maxP = playersMax !== '' ? parseInt(playersMax, 10) : 999999;
    const q = searchQuery.trim().toLowerCase();
    const verQ = versionFilter.trim().toLowerCase();

    // 1. Фильтрация
    let result = allServers.filter(server => {
      // Поиск по строке
      if (q) {
        const match =
          server.ip.toLowerCase().includes(q) ||
          String(server.port).includes(q) ||
          server.version.toLowerCase().includes(q);
        if (!match) return false;
      }

      // Тип поиска
      if (searchType === 'vuln') {
        if (server.vulnLevel !== selectedVuln) return false;
      } else {
        if (server.players < minP || server.players > maxP) return false;
        if (verQ && !server.version.toLowerCase().includes(verQ)) return false;
      }

      return true;
    });

    // 2. Сортировка
    result.sort((a, b) => {
      switch (sortBy) {
        case 'online-desc':
          return b.players - a.players;
        case 'online-asc':
          return a.players - b.players;
        case 'version':
          return a.version.localeCompare(b.version);
        case 'vuln': {
          const rank = { high: 3, medium: 2, low: 1 };
          return (rank[b.vulnLevel] || 0) - (rank[a.vulnLevel] || 0);
        }
        default:
          return 0;
      }
    });

    return result;
  }, [allServers, searchQuery, searchType, selectedVuln, playersMin, playersMax, versionFilter, sortBy]);

  // Ограничиваем список выбранным лимитом (10, 30, 50)
  const displayedServers = useMemo(() => {
    return filteredAndSortedServers.slice(0, serverLimit);
  }, [filteredAndSortedServers, serverLimit]);

  // Копирование всех отображаемых IP
  const handleCopyAllIps = useCallback(() => {
    if (displayedServers.length === 0) {
      showToast(isEn ? 'No servers to copy' : 'Нет серверов для копирования');
      return;
    }
    const ips = displayedServers.map(s => `${s.ip}:${s.port}`).join('\n');
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(ips).then(() => {
        showToast(isEn ? `Copied ${displayedServers.length} IP addresses` : `Скопировано ${displayedServers.length} IP адресов`);
      });
    } else {
      const temp = document.createElement('textarea');
      temp.value = ips;
      document.body.appendChild(temp);
      temp.select();
      document.execCommand('copy');
      document.body.removeChild(temp);
      showToast(isEn ? `Copied ${displayedServers.length} IP addresses` : `Скопировано ${displayedServers.length} IP адресов`);
    }
  }, [displayedServers, isEn, showToast]);

  // Экспорт списка в TXT файл
  const handleExportTxt = useCallback(() => {
    if (displayedServers.length === 0) {
      showToast(isEn ? 'No servers to export' : 'Нет серверов для экспорта');
      return;
    }
    const lines = displayedServers.map(
      s => `${s.ip}:${s.port} | ${s.version} | Online: ${s.players} | ${s.vulnBadge || ''}`
    );
    const blob = new Blob([lines.join('\n')], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `servers_parser_${Date.now()}.txt`;
    link.click();
    URL.revokeObjectURL(url);
    showToast(isEn ? 'Servers list downloaded as TXT' : 'Список серверов скачан в TXT');
  }, [displayedServers, isEn, showToast]);

  return (
    <PageTransition>
      <div className="sp-page-wrapper">
        <div className="sp-container">
          {/* Херо секция */}
          <ServersParserHero />

          {/* 4 KPI карточки */}
          <ServersParserKpiStats />

          {/* Главная рабочая область: Конфигурация + Таблица */}
          <div className="sp-workspace-grid">
            {/* Левая панель конфигурации */}
            <ServersParserConfigCard
              searchType={searchType}
              onSearchTypeChange={setSearchType}
              selectedVuln={selectedVuln}
              onSelectVuln={handleSelectVuln}
              playersMin={playersMin}
              playersMax={playersMax}
              onPlayersMinChange={setPlayersMin}
              onPlayersMaxChange={setPlayersMax}
              versionFilter={versionFilter}
              onVersionFilterChange={setVersionFilter}
              serverLimit={serverLimit}
              onServerLimitChange={setServerLimit}
              onStartParsing={handleStartParsing}
              isProcessing={isProcessing}
            />

            {/* Правая колонка: Поиск, Тулбар и Таблица */}
            <div className="sp-table-column-wrap">
              <ServersParserSearchCapsule
                value={searchQuery}
                onChange={setSearchQuery}
              />

              <ServersParserToolbar
                resultsCount={displayedServers.length}
                sortBy={sortBy}
                onSortChange={setSortBy}
                onCopyAllIps={handleCopyAllIps}
                onExportTxt={handleExportTxt}
              />

              <ServersParserTable
                servers={displayedServers}
                onCopyIp={handleCopyIp}
                hasParsed={hasParsed}
                isProcessing={isProcessing}
              />
            </div>
          </div>

          {/* Советы по парсингу */}
          <ServersParserTips />
        </div>

        {/* Legal Terms of Service Modal */}
        <TermsOfServiceModal
          isOpen={isTermsModalOpen}
          onClose={() => setIsTermsModalOpen(false)}
          onAccept={() => {
            setIsTermsModalOpen(false);
            executeStartParsing();
          }}
          onDecline={() => setIsTermsModalOpen(false)}
          t={t}
        />
      </div>
    </PageTransition>
  );
};

export default MonitoringParserPage;